import get from 'lodash/get';
import isNil from 'lodash/isNil';
import mapKeysDeep from 'map-keys-deep';
import invert from 'lodash/invert';
import { sortDeadlines } from './timestamp';

const fieldKeyMapping = {
  user_firstname: 'userFirstName',
  user_lastname: 'userLastName',
  user_email: 'userEmail',
  last_updated: 'lastUpdated',
  field_he_schlr_url: 'url',
  field_he_schlr_add_info: 'addInfo',
  publish_status: 'publishStatus',
  field_he_schlr_name: 'name',
  field_he_schlr_award_type: 'awardType',
  field_he_schlr_aw_amount_min: 'awAmountMin',
  field_he_schlr_aw_amount_max: 'awAmountMax',
  field_he_schlr_renewable: 'renewable',
  field_he_schlr_gpa_min: 'gpaMin',
  field_he_schlr_gpa_max: 'gpaMax',
  field_he_schlr_gpa_rank_match: 'gpaRankMatch',
  field_he_schlr_class_rank_min: 'classRankMin',
  field_he_schlr_class_rank_max: 'classRankMax',
  field_he_schlr_sat_gpa_match: 'satGpaMatch',
  field_he_schlr_sat_min: 'satMin',
  field_he_schlr_sat_max: 'satMax',
  field_he_schlr_sat_act_match: 'satActMatch',
  field_he_schlr_act_min: 'actMin',
  field_he_schlr_act_max: 'actMax',
  field_he_schlr_audience: 'audience',
  calc_he_schlr_normalized_states: 'audienceStates',
  meritBasedScholarships: 'scholarships',
};

const scholarshipKeyMapping = Object.assign({}, fieldKeyMapping, invert(fieldKeyMapping));

export default class DataMapper {
  constructor(data) {
    if (data) {
      this.raw = data;
    }
  }

  raw = null;

  /* low-level methods */

  getNonNilString = (item, path) => {
    const value = get(item, path, '');

    return isNil(value) ? '' : value;
  };

  getEmptyField = () => ({ value: '', label: '' });

  getFieldValue = (path, isBoolean, allowBooleanAsNull) => {
    const field = this.getEmptyField();
    const defaultBooleanVal = allowBooleanAsNull ? null : false;
    let value = get(this.raw, path, isBoolean ? defaultBooleanVal : '');

    if (isBoolean) {
      const isYesNo = /^(yes|no)$/gi.test(value);

      if (isYesNo) {
        value = value === 'yes' ? true : false;
      } else if (
        !allowBooleanAsNull ||
        (allowBooleanAsNull && value !== null && value !== undefined)
      ) {
        value = Boolean(value);
      }
    } else if (isNil(value)) {
      value = '';
    } else {
      value = String(value);
    }

    field.value = value;

    return field;
  };

  getArray = (path) => {
    const field = this.getEmptyField();
    let value = get(this.raw, path, null);

    if (!Array.isArray(value)) {
      value = [];
    }
    field.value = value;
    return field;
  };

  getPercentage = (numerator, denominator) => {
    if (!numerator || !denominator) {
      return { value: '' };
    }
    if (+denominator < +numerator) {
      return { value: '' };
    }
    return { value: String(Math.round((numerator / denominator) * 100)) };
  };

  transformScholarship = (obj) => {
    const transformed = mapKeysDeep(obj, (key) => {
      return scholarshipKeyMapping[key] || key;
    });
    return transformed;
  };

  getScholarships = () => {
    const scholarships = get(this.raw, 'scholarship_info', {});

    return {
      lastUpdated: get(scholarships, 'last_updated'),
      url: get(scholarships, 'field_he_schlr_info_inst_ref.field_he_schlr_url', ''),
      addInfo: get(
        scholarships,
        'field_he_schlr_info_inst_ref.field_he_schlr_add_info',
        '',
      ),
      scholarships: get(
        scholarships,
        'field_he_schlr_info_inst_ref.scholarships',
        [],
      ).map((scholarship) => {
        return this.transformScholarship(scholarship);
      }),
    };
  };

  getStateField = (root) => {
    const field = this.getEmptyField();

    field.value = get(this.raw, `${root}.field_state_ref.field_state_short_name`, '');

    return field;
  };

  getMajorField = (major, path, isBoolean) => {
    const field = this.getEmptyField();
    field.value = get(major, path, isBoolean ? false : '');
    return field;
  };

  getTopFiveAreasOfStudy = () => {
    const topAreasOfStudy = {
      1: this.getFieldValue(
        'studentbody.field_he_studentbodyid_ref.field_he_popularareaofstudy1',
      ),
      2: this.getFieldValue(
        'studentbody.field_he_studentbodyid_ref.field_he_popularareaofstudy2',
      ),
      3: this.getFieldValue(
        'studentbody.field_he_studentbodyid_ref.field_he_popularareaofstudy3',
      ),
      4: this.getFieldValue(
        'studentbody.field_he_studentbodyid_ref.field_he_popularareaofstudy4',
      ),
      5: this.getFieldValue(
        'studentbody.field_he_studentbodyid_ref.field_he_popularareaofstudy5',
      ),
    };

    const hasTopAreasOfStudy = Object.values(topAreasOfStudy).find(
      (area) => area.value !== '',
    );
    return hasTopAreasOfStudy ? topAreasOfStudy : null;
  };

  getMajors = () => {
    const majors = get(this.raw, 'majors', []) || [];

    return majors.map((major) => {
      return {
        majorName: this.getMajorField(
          major,
          'field_he_schoolmajor_us_ref.pr_major.field_pr_major_name',
        ),
        areaOfStudy: this.getMajorField(
          major,
          'field_he_schoolmajor_us_ref.area_of_study.field_pr_area_of_study.field_pr_name_area_of_study',
        ),
        degreeOffering: {
          associates: this.getMajorField(major, 'field_he_associates_us', true),
          bachelors: this.getMajorField(major, 'field_he_bachelors_us', true),
          masters: this.getMajorField(major, 'field_he_masters_us', true),
          doctorates: this.getMajorField(major, 'field_he_doctorates_us', true),
          diploma: this.getMajorField(major, 'field_he_diplomas_us', true),
          certificates: this.getMajorField(major, 'field_he_certificates_us', true),
          gradCertificates: this.getMajorField(
            major,
            'field_he_gradcertificates_us',
            true,
          ),
        },
      };
    });
  };

  getMinors = () => {
    const minors = get(this.raw, 'field_he_minors_ref', []) || [];

    return minors.map((minor) => get(minor, 'name', ''));
  };

  getLastUpdatedMajor = () => {
    const majors = get(this.raw, 'majors', []) || [];
    const timestamps = majors.map((major) => +get(major, 'last_updated', ''));

    return Math.max(...timestamps);
  };

  getAddress = (addressType) => {
    let root;
    let path;

    if (addressType === 'financialaid') {
      root =
        'undergraduate_financial_aid.field_he_undergradfinaidid_ref.contact.field_he_finaidcontact_ref.address';
    } else {
      root = 'undergraduate_admissions.field_he_undergradadmission_ref';
    }

    if (addressType === 'admissions') {
      path = `${root}.admissions_contact.field_he_admissions_contact_ref.address`;
    } else if (addressType === 'application') {
      path = `${root}.app_address_ref`;
    } else {
      path = root;
    }

    return {
      country: this.getFieldValue(`${path}.field_country_ref.name`),
      state: this.getStateField(path),
      city: this.getFieldValue(`${path}.field_city`),
      zip: this.getFieldValue(`${path}.field_postalcode`),
      street1: this.getFieldValue(`${path}.field_addressline1`),
      street2: this.getFieldValue(`${path}.field_addressline2`),
      email: this.getFieldValue(`${path}.field_emailaddress`),
      fax: this.getFieldValue(`${path}.field_faxnumber`),
      phoneNumber: this.getFieldValue(`${path}.field_phonenumber`),
      tollFreeNumber: this.getFieldValue(`${path}.field_tollfreenumber`),
      url: this.getFieldValue(`${path}.field_url`),
    };
  };

  getPerson = (contactType) => {
    let path;

    if (contactType === 'financialaid') {
      path =
        'undergraduate_financial_aid.field_he_undergradfinaidid_ref.contact.field_he_finaidcontact_ref.contact';
    } else {
      path =
        'undergraduate_admissions.field_he_undergradadmission_ref.admissions_contact.field_he_admissions_contact_ref.contact';
    }

    return {
      namePrefix: this.getFieldValue(`${path}.field_nameprefix`),
      firstName: this.getFieldValue(`${path}.field_firstname`),
      middleName: this.getFieldValue(`${path}.field_middlename`),
      lastName: this.getFieldValue(`${path}.field_lastname`),
      jobTitle: this.getFieldValue(`${path}.field_jobtitle`),
      jobDescription: this.getFieldValue(`${path}.field_jobdescription`),
    };
  };

  getTestScores = () => {
    const scores =
      get(
        this.raw,
        'undergraduate_admissions.field_he_undergradadmission_ref.test.field_he_test_us',
        [],
      ) || [];

    return scores.map((score) => ({
      type: get(score, 'field_he_testtype_id'),
      25: get(score, 'field_he_low'),
      50: get(score, 'field_he_average'),
      75: get(score, 'field_he_high_score'),
    }));
  };

  getDeadlines = (path = 'deadlines', key = 'deadline') => {
    const deadlines = get(this.raw, path, []) || [];
    return sortDeadlines(
      deadlines.map((item) => {
        const deadline = get(item, key, {});
        return {
          type: get(deadline, 'field_he_deadlinetype', ''),
          description: get(deadline, 'field_he_typeotherdescription', ''),
          month: String(get(deadline, 'field_he_month_of_the_deadline', '')),
          day: String(get(deadline, 'field_he_day_of_the_deadline', '')),
          term: get(deadline, 'field_he_ca_term', ''),
        };
      }),
    );
  };

  getAtletics = () => {
    const sports = get(this.raw, 'sports', []) || [];
    return sports.map((item) => {
      const sport = get(item, 'he_school_sport_us', {});
      return {
        type: this.getNonNilString(sport, 'field_he_sport_name'),
        men: {
          association: this.getNonNilString(sport, 'field_he_assocmen_id'),
          division: this.getNonNilString(sport, 'field_he_divmen_id'),
          conference: this.getNonNilString(sport, 'field_he_confmen_id'),
          others: {
            club: Boolean(get(sport, 'field_he_clubmen')),
            intramural: Boolean(get(sport, 'field_he_intramuralmen')),
          },
        },
        women: {
          association: this.getNonNilString(sport, 'field_he_assocwomen_id'),
          division: this.getNonNilString(sport, 'field_he_divwomen_id'),
          conference: this.getNonNilString(sport, 'field_he_confwomen_id'),
          others: {
            club: Boolean(get(sport, 'field_he_clubwomen')),
            intramural: Boolean(get(sport, 'field_he_intramuralwomen')),
          },
        },
        coed: {
          association: this.getNonNilString(sport, 'field_he_assoccoed_id'),
          division: this.getNonNilString(sport, 'field_he_divcoed_id'),
          conference: this.getNonNilString(sport, 'field_he_confcoed_id'),
          others: {
            club: Boolean(get(sport, 'field_he_clubcoed')),
            intramural: Boolean(get(sport, 'field_he_intramuralcoed')),
          },
        },
      };
    });
  };

  getCostByProfileType = (profiles, profileType) => {
    const found = profiles.find(
      (item) => get(item, 'field_he_financialaidprofiletype') === profileType,
    );

    if (found) {
      const field = this.getEmptyField();
      field.value = String(get(found, 'field_he_value', ''));
      return field;
    }

    return this.getEmptyField();
  };

  getApplicationRequirements = (group) => {
    const qualifications =
      get(
        this.raw,
        'undergraduate_admissions.field_he_undergradadmission_ref.qualification_data.field_he_qualification_data_us',
        [],
      ) || [];
    const grouping = qualifications.filter(
      (item) =>
        get(item, 'field_he_qualification_grouping', '').toLowerCase() ===
        group.toLowerCase(),
    );

    return grouping.map((item) => ({
      type: get(item, 'field_he_qualification_type', ''),
      value: get(item, 'field_he_requiredoptrecommend', ''),
    }));
  };

  getRecommendedCourses = () => {
    const items =
      get(
        this.raw,
        'undergraduate_admissions.field_he_undergradadmission_ref.high_school_prerequisites.field_he_highschoolprerequisites',
        [],
      ) || [];

    return items.map((item) => ({
      type: this.getNonNilString(item, 'field_he_prerequisitetype', ''),
      recomendationYears:
        this.getNonNilString(item, 'recomendationYears', '') ||
        this.getNonNilString(item, 'field_he_yearsrecommended', ''),
      requiredYears:
        this.getNonNilString(item, 'requiredYears', '') ||
        this.getNonNilString(item, 'field_he_yearsrequired', ''),
    }));
  };

  getApplicationFactors = () => {
    const items =
      get(
        this.raw,
        'undergraduate_admissions.field_he_undergradadmission_ref.admission_factor_data.field_he_admission_factor_data',
        [],
      ) || [];

    return items.map((item) => ({
      type: this.getNonNilString(item, 'field_he_admissionfactortype'),
      importance: this.getNonNilString(item, 'field_he_importance'),
    }));
  };

  getServiceData = (services, name) => {
    const field = this.getEmptyField();

    if (!services || !name) {
      field.value = false;
    } else {
      const found = services.find((item) => get(item, 'name') === name);

      field.value = !!found;
    }

    return field;
  };

  /* mid-level methods */

  getOverview = () => ({
    schoolName: this.getFieldValue('general.field_he_name'),
    openingStatement: this.getFieldValue('general.field_he_missionstatement'),
    website: this.getFieldValue('address.field_url'),
    city: this.getFieldValue('address.field_city'),
    country: this.getFieldValue('address.field_country_ref.name'),
    schoolType: {
      level: this.getFieldValue('general.field_he_level'),
      control: this.getFieldValue('general.field_he_control_id'),
      type: this.getFieldValue('general.field_he_type'),
    },
    undergraduateEnrollment: {
      women: this.getFieldValue(
        'studentbody.field_he_studentbodyid_ref.field_he_numugfemales',
      ),
      men: this.getFieldValue(
        'studentbody.field_he_studentbodyid_ref.field_he_numugmales',
      ),
    },
    studentFacultyRatio: this.getFieldValue(
      'studentbody.field_he_studentbodyid_ref.field_he_studentfacultyratio',
    ),
    campusSurroundings: {
      citySize: this.getFieldValue('general.field_he_city_size'),
    },
    testScores: this.getTestScores(),
    gpa: {
      25: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.undergraduate_admissions_us.field_he_gpa_25th_percentile',
      ),
      50: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.undergraduate_admissions_us.field_he_gpaaverage',
      ),
      75: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.undergraduate_admissions_us.field_he_gpa_75th_percentile',
      ),
    },
    applicationMailingAddress: this.getAddress('application'),
    admissionsOffice: {
      ...this.getPerson('admissions'),
      ...this.getAddress('admissions'),
    },
    financialAid: {
      ...this.getPerson('financialaid'),
      ...this.getAddress('financialaid'),
    },
    navianceId: this.getFieldValue('general.field_he_navianceid'),
    religiousAffiliation: this.getFieldValue('general.field_he_religious_affiliation'),
    actCode: this.getFieldValue('general.field_he_act_code'),
    hiid: this.getFieldValue('general.field_hiid'),
  });

  getStudies = () => {
    const root = 'special_service.field_he_specialserviceid_ref';
    const services = get(
      this.raw,
      `${root}.he_special_services_id_ref.field_he_special_services_id_ref`,
      [],
    );
    const specialFieldPath = `${root}.he_special_service_us`;

    return {
      avgClassSize: this.getFieldValue('general.field_he_avg_class_size'),
      programsOnline: this.getFieldValue('general.field_he_programsonline_id'),
      studentFacultyRatio: this.getFieldValue(
        'studentbody.field_he_studentbodyid_ref.field_he_studentfacultyratio',
      ),
      studentRetention: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.undergraduate_admissions_us.field_he_retentionratefulltime',
      ),
      graduationRate4: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.graduate_rate.field_he_gradratebachelors4yrs',
      ),
      graduationRate6: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.graduate_rate.field_he_gradratebachelors6yrs',
      ),

      topAreasOfStudy: this.getTopFiveAreasOfStudy(),

      lastUpdatedMajor: this.getLastUpdatedMajor(),

      majors: this.getMajors(),

      minors: this.getMinors(),

      studyOptions: {
        studyAbroadCredit: this.getServiceData(services, 'Study Abroad Credit'),
        studyAbroadNonCredit: this.getServiceData(services, 'Study Abroad NonCredit'),
        graduateStudyAbroadCredit: this.getServiceData(
          services,
          'Graduate Study Abroad Credit',
        ),
        graduateStudyAbroadNonCredit: this.getServiceData(
          services,
          'Graduate Study Abroad NonCredit',
        ),
        studyAbroad: this.getServiceData(services, 'Study Abroad'),
        graduateStudyAbroad: this.getServiceData(services, 'Graduate Study Abroad'),
        teacherCertificationPrograms: this.getFieldValue(
          `${specialFieldPath}.field_he_teachercertpgms`,
          true,
        ),
        combinedDegreePrograms: this.getFieldValue(
          `${specialFieldPath}.field_he_combineddegreepgms`,
          true,
        ),
        domesticStudyAwayPrograms: this.getFieldValue(
          `${specialFieldPath}.field_he_domstudyawaypgms`,
          true,
        ),
        weekendEveningPrograms: this.getFieldValue(
          `${specialFieldPath}.field_he_weekendeveningpgms`,
          true,
        ),
        acceleratedProgram: this.getFieldValue(
          `${specialFieldPath}.field_he_acceleratedprogram`,
          true,
        ),
        doubleMajor: this.getFieldValue(`${specialFieldPath}.field_he_doublemajor`, true),
        externalDegreeProgram: this.getFieldValue(
          `${specialFieldPath}.field_he_externaldegreeprogram`,
          true,
        ),
        honorsProgram: this.getFieldValue(
          `${specialFieldPath}.field_he_honorsprogram`,
          true,
        ),
        independentStudy: this.getFieldValue(
          `${specialFieldPath}.field_he_independentstudy`,
          true,
        ),
        internships: this.getFieldValue(`${specialFieldPath}.field_he_internships`, true),
        weekendCollege: this.getFieldValue(
          `${specialFieldPath}.field_he_weekendcollege`,
          true,
        ),
        cooperativeEducationPrograms: this.getFieldValue(
          `${specialFieldPath}.field_he_cooperativeedupgms`,
          true,
        ),
      },
      jobPlacementRate: {
        greaterThan1Year: this.getFieldValue(
          'undergraduate_admissions.field_he_undergradadmission_ref.field_he_job_plcmnt_rt_1_yr_gr',
        ),
        lessThan1Year: this.getFieldValue(
          'undergraduate_admissions.field_he_undergradadmission_ref.field_he_job_plcmnt_rt_less_1_yr',
        ),
        lessThan6Months: this.getFieldValue(
          'undergraduate_admissions.field_he_undergradadmission_ref.field_he_job_plcmnt_rt_less_6_mo',
        ),
      },
      placement: {
        advanced: this.getFieldValue(
          'undergraduate_admissions.field_he_undergradadmission_ref.undergraduate_admissions_us.field_he_ceebadvancedplacementid',
        ),
        clep: this.getFieldValue(
          'undergraduate_admissions.field_he_undergradadmission_ref.undergraduate_admissions_us.field_he_clepsubjectplacement_id',
        ),
        international: this.getFieldValue(
          'undergraduate_admissions.field_he_undergradadmission_ref.undergraduate_admissions_us.field_he_intlbaccalaureateplcmid',
        ),
      },
      isStarfishClient: this.getFieldValue('general.field_is_starfish_client', true),
    };
  };

  getAdmissions = () => ({
    acceptanceRate: {
      applicationsAccepted: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.admission_general.field_he_applications_accepted',
      ),
      applicationsReceived: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.admission_general.field_he_applications_received',
      ),
    },
    importantPolicies: {
      admissionsPolicy: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.undergraduate_admissions_us.field_he_admissionspolicy_id',
      ),
      notificationPolicy: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.undergraduate_admissions_us.field_he_notificationpolicy_id',
      ),
      registrationPolicy: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.undergraduate_admissions_us.field_he_registrationpolicy_id',
      ),
      earlyDecision: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.undergraduate_admissions_us.field_he_earlydecision',
        true,
        true,
      ),
      waitingList: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.undergraduate_admissions_us.field_he_waitinglistused',
        true,
        true,
      ),
      testOptional: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.undergraduate_admissions_us.field_he_testoptional',
        true,
        true,
      ),
      commonAppMember: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.field_he_common_app_member',
        true,
        true,
      ),
      coalitionAppMember: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.field_he_coalition_app_member',
        true,
        true,
      ),
    },
    deadlines: this.getDeadlines(),
    fees: {
      applicationFee: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.admission_general.field_he_applicationfee',
      ),
      deposit: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.admission_general.field_he_deposit',
      ),
      transferDepositFee: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.undergraduate_admissions_us.field_he_transferappdeposit',
      ),
      transferApplicationFee: this.getFieldValue(
        'undergraduate_admissions.field_he_undergradadmission_ref.undergraduate_admissions_us.field_he_transferapplicationfee',
      ),
      internationalApplicationFee: this.getFieldValue(
        'international_admissions.field_he_intadmissions_ref.admission_general_us.field_he_applicationfee',
      ),
      internationalDepositFee: this.getFieldValue(
        'international_admissions.field_he_intadmissions_ref.admission_general_us.field_he_deposit',
      ),
    },
    applicationRequirements: {
      freshman: this.getApplicationRequirements('Freshman'),
      transfer: this.getApplicationRequirements('Transfer'),
      international: this.getApplicationRequirements('International'),
    },
    commonBlackCollegeApp: {
      isCBCA: this.getFieldValue('general.field_he_is_cbca', true, true),
    },
    recommendedCourses: this.getRecommendedCourses(),
    applicationFactors: this.getApplicationFactors(),
    lastUpdated: this.getFieldValue('undergraduate_admissions.last_updated'),
  });

  getStudentLife = () => {
    const servicesRoot = 'special_service.field_he_specialserviceid_ref';
    const organizationsRoot =
      'student_organization.field_he_studentorg_id_ref.field_he_orgrepresented';
    const services = get(
      this.raw,
      `${servicesRoot}.he_special_services_id_ref.field_he_special_services_id_ref`,
      [],
    );
    const organizations = get(this.raw, organizationsRoot, []);
    const specialFieldPath = `${servicesRoot}.he_special_service_us`;

    return {
      coedStatus: this.getFieldValue('general.field_he_coedstatus'),
      schoolSize: {
        undergraduateTotal: this.getFieldValue(
          'studentbody.field_he_studentbodyid_ref.calc_he_undergraduatesize',
        ),
        undergraduateWomen: this.getFieldValue(
          'studentbody.field_he_studentbodyid_ref.field_he_numugfemales',
        ),
        undergraduateMen: this.getFieldValue(
          'studentbody.field_he_studentbodyid_ref.field_he_numugmales',
        ),
        graduateWomen: this.getFieldValue(
          'studentbody.field_he_studentbodyid_ref.field_he_numgradfemales',
        ),
        graduateMen: this.getFieldValue(
          'studentbody.field_he_studentbodyid_ref.field_he_numgradmales',
        ),
      },
      nearestCity: {
        city: this.getFieldValue('general.field_he_nearestcity'),
        distance: this.getFieldValue('general.field_he_nearestmajorcitymiles'),
      },
      ethnicity: {
        africanAmerican: this.getFieldValue(
          'studentbody.field_he_studentbodyid_ref.field_he_pctafricanam',
        ),
        hispanic: this.getFieldValue(
          'studentbody.field_he_studentbodyid_ref.field_he_pcthispanic',
        ),
        nativePacificIslander: this.getFieldValue(
          'studentbody.field_he_studentbodyid_ref.field_he_pctnativepacificisland',
        ),
        twoOrMoreRaces: this.getFieldValue(
          'studentbody.field_he_studentbodyid_ref.field_he_pcttwoormoreraces',
        ),
        caucasian: this.getFieldValue(
          'studentbody.field_he_studentbodyid_ref.field_he_pctcaucasian',
        ),
        asian: this.getFieldValue(
          'studentbody.field_he_studentbodyid_ref.field_he_pctasian',
        ),
        nativeAmerican: this.getFieldValue(
          'studentbody.field_he_studentbodyid_ref.field_he_pctnativeam',
        ),
        nativeUnknown: this.getFieldValue(
          'studentbody.field_he_studentbodyid_ref.field_he_pctraceunknown',
        ),
        nonResidentAlien: this.getFieldValue(
          'studentbody.field_he_studentbodyid_ref.field_he_pctnonresidentalien',
        ),
        international: this.getFieldValue(
          'studentbody.field_he_studentbodyid_ref.field_he_pctinternational',
        ),
      },
      ageData: this.getFieldValue(
        'studentbody.field_he_studentbodyid_ref.field_he_pctstudentsgt24yrsold',
      ),
      outOfStateData: this.getFieldValue(
        'studentbody.field_he_studentbodyid_ref.field_he_pctoutofstate',
      ),
      housingData: {
        campusHousingCapacity: this.getFieldValue(
          'housing.field_he_housing_ref.field_he_camphouscap',
        ),
        liveOnCampus: this.getFieldValue(
          'studentbody.field_he_studentbodyid_ref.field_he_pctliveoncamp',
        ),
        freshmanMustLiveOnCampus: this.getFieldValue(
          'housing.field_he_housing_ref.field_he_freshmenmustliveoncampu',
          true,
          true,
        ),
        sophomoresMustLiveOnCampus: this.getFieldValue(
          'housing.field_he_housing_ref.field_he_sophomoresmustliveoncam',
          true,
          true,
        ),
        juniorsMustLiveOnCampus: this.getFieldValue(
          'housing.field_he_housing_ref.field_he_juniorsmustliveoncampus',
          true,
          true,
        ),
        seniorsMustLiveOnCampus: this.getFieldValue(
          'housing.field_he_housing_ref.field_he_seniorsmustliveoncampus',
          true,
          true,
        ),
      },
      greekLife: {
        fraternaties: this.getFieldValue(
          'student_organization.field_he_studentorg_id_ref.field_he_numsocialfrat',
        ),
        sororities: this.getFieldValue(
          'student_organization.field_he_studentorg_id_ref.field_he_numsocialsor',
        ),
      },
      services: {
        academicCareerCounseling: this.getServiceData(
          services,
          'Academic/Career Counseling',
        ),
        accommodationsForStudentsWithADHD: this.getServiceData(
          services,
          'Accommodations for Students with ADHD',
        ),
        assistanceForHearingImpaired: this.getServiceData(
          services,
          'Assistance for Hearing Impaired',
        ),
        assistanceForMobilityImpaired: this.getServiceData(
          services,
          'Assistance for Mobility Impaired',
        ),
        assistanceForVisuallyImpaired: this.getServiceData(
          services,
          'Assistance for Visually Impaired',
        ),
        bookOnTapeForRFBD: this.getServiceData(services, 'Book on Tape for RFBD'),
        calculatorsAllowedInExams: this.getServiceData(
          services,
          'Calculators Allowed in Exams',
        ),
        computersAllowedInExams: this.getServiceData(
          services,
          'Computers Allowed in Exams',
        ),
        dayCareServices: this.getServiceData(services, 'Day Care Services'),
        dictionaryAllowedInExams: this.getServiceData(
          services,
          'Dictionary Allowed in Exams',
        ),
        ESL: this.getServiceData(services, 'ESL (English as Second Language)'),
        healthServicesAvailable: this.getServiceData(
          services,
          'Health Services Available',
        ),
        internshipsWithPay: this.getServiceData(services, 'Internships with Pay'),
        internshipsWithoutPay: this.getServiceData(services, 'Internships without Pay'),
        jobPlacement: this.getServiceData(services, 'Job Placement'),
        LDSpecialists: this.getServiceData(services, 'LD Specialists'),
        learningCenter: this.getServiceData(services, 'Learning Center'),
        libraryFacilities: this.getServiceData(services, 'Library Facilities'),
        peerTutors: this.getServiceData(services, 'Peer Tutors'),
        professionalTutors: this.getServiceData(services, 'Professional Tutors'),
        readingMachine: this.getServiceData(services, 'Reading Machine'),
        remedialServices: this.getServiceData(services, 'Remedial Services'),
        spellcheckerAllowedInExam: this.getServiceData(
          services,
          'Spellchecker Allowed in Exam',
        ),
        studentEmployment: this.getServiceData(services, 'Student Employment'),
        tapeRecordingInClass: this.getServiceData(services, 'Tape Recording in Class'),
        tapingOfBooksNotFromRFBD: this.getServiceData(
          services,
          'Taping of Books not from RFBD',
        ),
        veteran: this.getServiceData(services, 'Veteran'),
        aspergersAutismSupportServices: this.getServiceData(
          services,
          'Aspergers/Autism Support Services',
        ),
        ROTCArmy: this.getFieldValue(`${specialFieldPath}.field_he_rotcarmy`, true),
        ROTCNavy: this.getFieldValue(`${specialFieldPath}.field_he_rotcnavy`, true),
        ROTCAirforce: this.getFieldValue(`${specialFieldPath}.field_he_rotcair`, true),
        ROTCServicepersonOpportunityCollege: this.getFieldValue(
          `${specialFieldPath}.field_he_rotcservperoppcollege`,
          true,
        ),
        counselingServices: this.getFieldValue(
          `${servicesRoot}.field_he_counseling_services`,
          true,
        ),
        learningDifferencesSupport: this.getFieldValue(
          `${servicesRoot}.calc_he_learning_differences_support`,
          true,
        ),
      },
      computingResources: {
        numberOfMacsInComputerCenters: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_macsincomputercen',
        ),
        numberOfPCsInComputerCenters: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_pcsincomputercenters',
        ),
        numberOfMacsInDepartmentLabs: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_macsindepartmentlabs',
        ),
        numberOfPCsInDepartmentLabs: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_pcsindepartmentlabs',
        ),
        numberOfMacsInLibraries: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_macsinlibrary',
        ),
        numberOfPCsInLibraries: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_pcsinlibrary',
        ),
        numberOfMacsInStudentCenters: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_macsinstudentcenters',
        ),
        numberOfPCsInStudentCenters: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_pcsinstudentcenters',
        ),
        numberOfMacsInResidences: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_macsinresidences',
        ),
        numberOfPCsInResidences: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_pcsinresidences',
        ),
        numberOfMacsInOther: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_macsinother',
        ),
        numberOfPCsInOther: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_pcsinother',
        ),
        internetAccessInLibrary: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_internetinlibrary',
          true,
        ),
        wirelessInternetAccess: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_internetwireless',
          true,
        ),
        internetAccessOnCentralCampus: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_internetincentralloc',
          true,
        ),
        internetAccessInResidenceHalls: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_internetinresidencehall',
          true,
        ),
        computerRequired: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_computerrequired',
          true,
        ),
        computerRequiredReason: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_computerrequiredreason',
        ),
        computerPurchaseOffered: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_purchaseplanoffered',
          true,
        ),
        preferredComputerType: this.getFieldValue(
          'computing_environment.field_he_comenvironment_ref.field_he_prefered_computer_type',
        ),
      },
      organizations: {
        academicClubs: this.getServiceData(organizations, 'Academic Clubs'),
        academicFratSor: this.getServiceData(organizations, 'Academic Frat./Sor.'),
        business: this.getServiceData(organizations, 'Business'),
        choir: this.getServiceData(organizations, 'Choir'),
        commuter: this.getServiceData(organizations, 'Commuter'),
        concertBand: this.getServiceData(organizations, 'Concert Band'),
        environmental: this.getServiceData(organizations, 'Environmental'),
        gayAndLesbian: this.getServiceData(organizations, 'LGBTQ+'),
        honorsAcademic: this.getServiceData(organizations, 'Honors/Academic'),
        international: this.getServiceData(organizations, 'International'),
        jazzBand: this.getServiceData(organizations, 'Jazz Band'),
        literaryMagazine: this.getServiceData(organizations, 'Literary Magazine'),
        marchingBand: this.getServiceData(organizations, 'Marching Band'),
        minorityCultural: this.getServiceData(organizations, 'Cultural Awareness'),
        minorityStudent: this.getServiceData(organizations, 'Minority Student Programs'),
        newspaper: this.getServiceData(organizations, 'Newspaper'),
        orchestra: this.getServiceData(organizations, 'Orchestra'),
        phiBetaKappa: this.getServiceData(organizations, 'Phi Beta Kappa'),
        physicallyChallenged: this.getServiceData(
          organizations,
          'Students with Disabilities',
        ),
        politicalActivism: this.getServiceData(organizations, 'Political Activism'),
        preDental: this.getServiceData(organizations, 'Pre-Dental'),
        preLaw: this.getServiceData(organizations, 'Pre-Law'),
        preMedical: this.getServiceData(organizations, 'Pre-Medical'),
        preVeterinary: this.getServiceData(organizations, 'Pre-Veterinary'),
        radioStation: this.getServiceData(organizations, 'Radio Station'),
        religious: this.getServiceData(organizations, 'Religious'),
        sportsIntramurals: this.getServiceData(organizations, 'Sports/Intramurals'),
        studentGovernment: this.getServiceData(organizations, 'Student Government'),
        substanceAbuseSupport: this.getServiceData(
          organizations,
          'Substance Abuse Support',
        ),
        televisionStation: this.getServiceData(organizations, 'Television Station'),
        theatreDrama: this.getServiceData(organizations, 'Theatre/Drama'),
        yearbook: this.getServiceData(organizations, 'Yearbook'),
        phiThetaKappa: this.getServiceData(organizations, 'Phi Theta Kappa'),
      },
      athletics: this.getAtletics(),
      campusPride: this.getFieldValue('general.field_he_lgbt_rating'),
      lastUpdated: this.getFieldValue('studentbody.last_updated'),
      location: {
        latitude: get(this.raw, 'general.field_he_location.lat', 0),
        longitude: get(this.raw, 'general.field_he_location.lon', 0),
      },
    };
  };

  getLatestYearCostData = (basePath, field, fieldPlus1year) => {
    const fieldValue = this.getFieldValue(`${basePath}.${field}`);
    const fieldPlus1yearValue = this.getFieldValue(`${basePath}.${fieldPlus1year}`);

    if (get(fieldPlus1yearValue, 'value', null)) {
      return fieldPlus1yearValue;
    }

    return fieldValue;
  };

  getCostsFeeYear = () => {
    const feeYear = this.getFieldValue(
      'undergraduate_financial_aid.field_he_undergradfinaidid_ref.field_he_feeyear',
    );

    if (get(feeYear, 'value', null) && !Number.isNaN(Number(feeYear.value))) {
      const finAidPath =
        'undergraduate_financial_aid.field_he_undergradfinaidid_ref.undergraduate_financial_aid';
      const inStateTuitionPlus1yearValue = this.getFieldValue(
        `${finAidPath}.field_he_instatetuitionplus1yr`,
      );
      const outOfStateTuitionPlus1yearValue = this.getFieldValue(
        `${finAidPath}.field_he_outofstatetuitionplus1y`,
      );
      if (
        get(inStateTuitionPlus1yearValue, 'value', null) &&
        get(outOfStateTuitionPlus1yearValue, 'value', null)
      ) {
        feeYear.value = `${Number(feeYear.value) + 1}`;
      }
    }

    return feeYear;
  };

  getCosts = () => {
    const profiles = get(
      this.raw,
      'undergraduate_financial_aid.field_he_undergradfinaidid_ref.financial_aid_general.field_he_financial_profile',
      [],
    );
    return {
      averageNetPrices: {
        '0-30': this.getCostByProfileType(profiles, 'Avg. Net Price (income $0-30k)'),
        '30-48': this.getCostByProfileType(profiles, 'Avg. Net Price (income $30k-48k)'),
        '48-75': this.getCostByProfileType(profiles, 'Avg. Net Price (income $48k-75k)'),
        '75-110': this.getCostByProfileType(
          profiles,
          'Avg. Net Price (income $75k-110k)',
        ),
        '110+': this.getCostByProfileType(profiles, 'Avg. Net Price (income >110k)'),
      },
      receivingAid: {
        grant: this.getCostByProfileType(profiles, '% Receiving Grant Aid'),
        pellGrant: this.getCostByProfileType(profiles, '% Receiving Pell Grant Aid'),
        studentLoan: this.getCostByProfileType(
          profiles,
          '% Receiving Federal Student Loan',
        ),
      },
      averageAmountOfAid: {
        grant: this.getCostByProfileType(profiles, 'Avg. Grant Amount'),
        pellGrant: this.getCostByProfileType(profiles, 'Avg. Pell Grant Amount'),
        studentLoan: this.getCostByProfileType(
          profiles,
          'Avg. Federal Student Loan Amount',
        ),
      },
      tuition: {
        inState: this.getLatestYearCostData(
          'undergraduate_financial_aid.field_he_undergradfinaidid_ref.undergraduate_financial_aid',
          'field_he_instatetuition',
          'field_he_instatetuitionplus1yr',
        ),
        outOfState: this.getLatestYearCostData(
          'undergraduate_financial_aid.field_he_undergradfinaidid_ref.undergraduate_financial_aid',
          'field_he_outofstatetuition',
          'field_he_outofstatetuitionplus1y',
        ),
        tuitionPerCreditHour: this.getLatestYearCostData(
          'undergraduate_financial_aid.field_he_undergradfinaidid_ref.undergraduate_financial_aid',
          'field_he_percredithourchargeamnt',
          'field_he_percredithourfeeplus1yr',
        ),
        feeYear: this.getCostsFeeYear(),
      },
      roomAndBoardCharge: this.getLatestYearCostData(
        'undergraduate_financial_aid.field_he_undergradfinaidid_ref.undergraduate_financial_aid',
        'field_he_typicalroomandboardchar',
        'field_he_typicalroomboardcharge1',
      ),
      roomCharge: this.getLatestYearCostData(
        'undergraduate_financial_aid.field_he_undergradfinaidid_ref.undergraduate_financial_aid',
        'field_he_typicalroomcharge',
        'field_he_typicalroomchargeplus1y',
      ),
      boardCharge: this.getLatestYearCostData(
        'undergraduate_financial_aid.field_he_undergradfinaidid_ref.undergraduate_financial_aid',
        'field_he_typicalboardcharge',
        'field_he_typicalboardchargeplus1',
      ),
      booksFee: this.getLatestYearCostData(
        'undergraduate_financial_aid.field_he_undergradfinaidid_ref.undergraduate_financial_aid',
        'field_he_books',
        'field_he_booksplus1yr',
      ),
      inStateFlatFee: this.getLatestYearCostData(
        'undergraduate_financial_aid.field_he_undergradfinaidid_ref.undergraduate_financial_aid',
        'field_he_instateflatfee',
        'field_he_instateflatfeeplus1yr',
      ),
      outOfStateFlatFee: this.getLatestYearCostData(
        'undergraduate_financial_aid.field_he_undergradfinaidid_ref.undergraduate_financial_aid',
        'field_he_outofstateflatfee',
        'field_he_outofstateflatfeeplus1y',
      ),
      lastUpdated: this.getFieldValue('undergraduate_financial_aid.last_updated'),
      meetsFullFinancialNeeds: this.getFieldValue(
        'undergraduate_financial_aid.field_he_undergradfinaidid_ref.field_he_meets_full_fin_needs',
        true,
      ),
    };
  };

  getTransferAdmissions = () => {
    return {
      lastUpdated: this.getFieldValue('transfer_admissions.last_updated'),
      overview: this.getFieldValue(
        'transfer_admissions.field_he_xfer_ref.general.field_he_xfer_overview',
      ),
      infoUrl: this.getFieldValue(
        'transfer_admissions.field_he_xfer_ref.general.field_he_xfer_info_url',
      ),
      supportUrl: this.getFieldValue(
        'transfer_admissions.field_he_xfer_ref.general.field_he_xfer_support_url',
      ),
      financialAidUrl: this.getFieldValue(
        'transfer_admissions.field_he_xfer_ref.financial_aid.field_he_xfer_fin_aid_url',
      ),
      creditInfoUrl: this.getFieldValue(
        'transfer_admissions.field_he_xfer_ref.policies_and_requirements.field_he_xfer_credit_info_url',
      ),
      undergraduateEnrollment: this.getFieldValue(
        'transfer_admissions.field_he_xfer_ref.student_data.field_he_xfer_in_enrollment',
      ),
      scholarshipsAvailable: this.getFieldValue(
        'transfer_admissions.field_he_xfer_ref.financial_aid.field_he_xfer_scholarships_avbl',
        true,
      ),
      deadlines: this.getDeadlines(
        'transfer_admissions.field_he_xfer_ref.deadlines.field_he_xfer_deadlines',
      ),
      graduationRate: this.getFieldValue(
        'transfer_admissions.field_he_xfer_ref.student_data.field_he_xfer_ft_8yr_grad_rate',
      ),
      enrollmentTerms: this.getFieldValue(
        'transfer_admissions.field_he_xfer_ref.general.field_he_xfer_sems_transfer',
      ),
      transferEnrollmentPercentage: this.getPercentage(
        this.getFieldValue(
          'transfer_admissions.field_he_xfer_ref.student_data.field_he_xfer_in_enrollment',
        ).value,
        this.getFieldValue(
          'studentbody.field_he_studentbodyid_ref.calc_he_undergraduatesize',
        ).value,
      ),
      creditCap: this.getFieldValue(
        'transfer_admissions.field_he_xfer_ref.policies_and_requirements.field_he_xfer_credit_cap',
      ),
      articulationAgreements: this.getArray(
        'transfer_admissions.field_he_xfer_ref.policies_and_requirements.field_he_xfer_artic_agr',
      ),
      admissionPolicies: this.getArray(
        'transfer_admissions.field_he_xfer_ref.policies_and_requirements.field_he_xfer_adm_policies',
      ),
      articulationAgreementsSystem: this.getArray(
        'transfer_admissions.field_he_xfer_ref.policies_and_requirements.field_he_xfer_artic_sys',
      ),
      dualAdmissions: this.getArray(
        'transfer_admissions.field_he_xfer_ref.policies_and_requirements.field_he_xfer_dual_admissions',
      ),
      minHomeCredits: this.getFieldValue(
        'transfer_admissions.field_he_xfer_ref.policies_and_requirements.field_he_xfer_min_home_credits',
      ),
      admissionsRequirements: this.getArray(
        'transfer_admissions.field_he_xfer_ref.policies_and_requirements.field_he_xfer_admissions_req',
      ),
      contactRefPhone: this.getFieldValue(
        'transfer_admissions.field_he_xfer_ref.contact.field_he_xfer_c_phone',
      ),
      contactRefEmail: this.getFieldValue(
        'transfer_admissions.field_he_xfer_ref.contact.field_he_xfer_c_email',
      ),
    };
  };

  /* high-level method */

  getState = () => ({
    overview: this.getOverview(),
    studies: this.getStudies(),
    admissions: this.getAdmissions(),
    studentLife: this.getStudentLife(),
    costs: this.getCosts(),
    transferAdmissions: this.getTransferAdmissions(),
    scholarships: this.getScholarships(),
  });
}
